import DeleteButton from '@pinecast/common/DeleteButton';
import type {ColorMap, CSS} from '@pinecast/styles';
import styled, {useColors} from '@pinecast/styles';
import * as React from 'react';

type Props = {
  $color: 'gray' | 'green' | 'blue' | 'red' | 'yellow';
  $hasDeleteButton: boolean;
  $size: 'small' | 'medium' | 'large';
};

const bgColors = (designColors: ColorMap) => ({
  gray: designColors.badgeGrayBackground,
  green: designColors.badgeGreenBackground,
  blue: designColors.badgeBlueBackground,
  red: designColors.badgeRedBackground,
  yellow: designColors.badgeYellowBackground,
});

const idleColors = (designColors: ColorMap) => ({
  gray: designColors.badgeGrayText,
  green: designColors.badgeGreenText,
  blue: designColors.badgeBlueText,
  red: designColors.badgeRedText,
  yellow: designColors.badgeYellowText,
});

const Tag_ = styled(
  'span',
  ({$color, $hasDeleteButton, $size}: Props, designColors) => {
    return {
      alignItems: 'center',
      alignSelf: 'center',
      backgroundColor: bgColors(designColors)[$color],
      borderRadius: $size === 'large' ? 5 : 4,
      border: `1px solid ${idleColors(designColors)[$color]}`,
      color: idleColors(designColors)[$color],
      display: 'inline-flex',
      flex: '0 0 0%',
      fontSize: $size === 'small' ? 11 : $size === 'medium' ? 13 : 16,
      fontWeight: 400,
      height: $size === 'small' ? 16 : $size === 'medium' ? 20 : 24,
      paddingTop: 0,
      paddingLeft: 6,
      paddingRight: $hasDeleteButton ? ($size === 'large' ? 2 : 1) : 6,
      paddingBottom: 0,
      transition: 'background-color 0.2s, color 0.2s',
      whiteSpace: 'nowrap',
    };
  },
  {className: 'Tag'},
);

const DBLabel = styled('span', {
  display: 'inline-block',
});

type ExternalProps = {
  children: React.ReactNode;
  color?: Props['$color'];
  deleteButton?: boolean;
  onDelete?: () => void;
  size?: Props['$size'];
  style?: CSS;
};

const Tag = (props: ExternalProps) => {
  const designColors = useColors();

  if (props.deleteButton && !props.onDelete) {
    throw new Error('Created Tag with deleteButton but not onDelete');
  }
  const dbSize = props.size === 'small' ? 16 : 20;
  return (
    <Tag_
      $color={props.color || 'gray'}
      $hasDeleteButton={props.deleteButton || false}
      $size={props.size || 'medium'}
      style={props.style}
    >
      {props.deleteButton ? (
        <React.Fragment>
          <DBLabel>{props.children}</DBLabel>
          <DeleteButton
            idleColor={idleColors(designColors)[props.color || 'gray']}
            onClick={props.onDelete as () => void}
            style={{
              alignSelf: 'center',
              height: dbSize,
              marginTop: 0,
              marginLeft: props.size === 'large' ? undefined : -2,
              width: dbSize,
            }}
          />
        </React.Fragment>
      ) : (
        props.children
      )}
    </Tag_>
  );
};

export default Tag;
